// The screens of two-factor authentication with an authenticator app, shared by the log in page, the invitation page and My account:
// the code step, setting the app up (QR code, the key to type, the first code) and the recovery codes shown once.
import { qrSvg } from '../shared/qr.js';
import { api, el, toast } from './format.js';

const groups = (secret) => secret.replace(/(.{4})/g, '$1 ').trim();

/** Shows the recovery codes (once) with Copy and Download; the button continues when the person says they are kept somewhere safe. */
export function showRecoveryCodes(root, codes, { onContinue, buttonLabel = 'Continue' }) {
  const text = codes.join('\n');
  const saved = el('input', { type: 'checkbox', id: 'tf-saved' });
  const go = el('button', { type: 'button', class: 'btn-primary', disabled: '' }, buttonLabel);
  saved.addEventListener('change', () => (go.disabled = !saved.checked));
  go.addEventListener('click', onContinue);
  root.replaceChildren(
    el('p', { class: 'muted' }, 'Two-factor authentication is on. Keep these ', el('strong', {}, 'recovery codes'), ' somewhere safe (a password manager, or printed). If you lose your phone, each code lets you in once. They are shown only now.'),
    el('div', { class: 'tf-codes', role: 'list' }, ...codes.map((c) => el('code', { role: 'listitem' }, c))),
    el('div', { class: 'tf-actions' },
      el('button', { type: 'button', class: 'btn-small', onclick: async () => { await navigator.clipboard.writeText(text); toast('Copied'); } }, 'Copy'),
      el('button', { type: 'button', class: 'btn-small', onclick: () => {
        const url = URL.createObjectURL(new Blob([`Kolvantage recovery codes\n\n${text}\n`], { type: 'text/plain' }));
        const a = el('a', { href: url, download: 'kolvantage-recovery-codes.txt' });
        document.body.append(a); a.click(); a.remove(); URL.revokeObjectURL(url);
      } }, 'Download')),
    el('label', { class: 'check', style: 'display:flex;gap:8px;align-items:center;margin:12px 0' }, saved, 'I saved my recovery codes'),
    go);
}

/**
 * Setting the app up for a `challenge` (from the log in, the invitation or My account): the QR code, the key, and the first code.
 * `onDone(result)` gets { user, recoveryCodes } once the code was right and the recovery codes were saved.
 */
export async function setupApp(root, { challenge, onDone, onRestart = () => location.reload(), buttonLabel = 'Continue' }) {
  root.replaceChildren(el('p', { class: 'muted' }, 'Preparing…'));
  let data;
  try {
    data = await api('/auth/2fa/setup', { method: 'POST', body: { challenge } });
  } catch (err) {
    root.replaceChildren(el('p', { class: 'form-error' }, err.message));
    return;
  }
  const qr = el('div', { class: 'tf-qr' });
  qr.innerHTML = qrSvg(data.otpauthUrl, { px: 200 }); // (drawn here from the address: only numbers and paths of our own SVG)
  const otp = el('input', { type: 'text', name: 'otp', inputmode: 'numeric', autocomplete: 'one-time-code', maxlength: '7', pattern: '[0-9 ]*', placeholder: '123456', 'aria-label': '6-digit code', class: 'tf-otp' });
  const error = el('p', { class: 'form-error', hidden: '' });
  const verify = el('button', { type: 'submit', class: 'btn-primary' }, 'Verify and turn on');
  const form = el('form', { class: 'stack' },
    el('label', { class: 'field' }, el('span', {}, '3. Enter the 6-digit code from the app'), otp), error, verify);
  form.addEventListener('submit', async (e) => {
    e.preventDefault();
    error.hidden = true;
    verify.disabled = true;
    try {
      const r = await api('/auth/2fa/enable', { method: 'POST', body: { challenge, otp: otp.value } });
      showRecoveryCodes(root, r.recoveryCodes, { buttonLabel, onContinue: () => onDone(r) });
    } catch (err) {
      error.textContent = err.message;
      error.hidden = false;
      verify.disabled = false;
      otp.select();
      if (err.restart) setTimeout(onRestart, 1800);
    }
  });
  root.replaceChildren(
    el('ol', { class: 'tf-steps muted' },
      el('li', {}, el('strong', {}, '1. Install an authenticator app'), ' on your phone: Google Authenticator, Microsoft Authenticator, Authy, 1Password…'),
      el('li', {}, el('strong', {}, '2. Scan this QR code'), ' with it (or type the key below).')),
    qr,
    el('div', { class: 'tf-secret' }, el('span', { class: 'muted small' }, 'Or type this key: '), el('code', {}, groups(data.secret)),
      el('button', { type: 'button', class: 'btn-small', onclick: async () => { await navigator.clipboard.writeText(data.secret); toast('Key copied'); } }, 'Copy')),
    form);
  otp.focus();
}

/** The code step of the log in: a code from the app, or a recovery code. `onDone(user)` gets the logged-in user. */
export function codeStep(root, { challenge, onDone, onRestart = () => location.reload() }) {
  const otp = el('input', { type: 'text', name: 'otp', inputmode: 'text', autocomplete: 'one-time-code', maxlength: '12', placeholder: '123456', 'aria-label': 'Code', class: 'tf-otp', autofocus: '' });
  const error = el('p', { class: 'form-error', hidden: '' });
  const submit = el('button', { type: 'submit', class: 'btn-primary' }, 'Log in');
  const form = el('form', { class: 'stack' },
    el('label', { class: 'field' }, el('span', {}, 'Code from your authenticator app'), otp,
      el('small', { class: 'muted' }, 'Open the app and type the 6-digit code for Kolvantage. Lost your phone? Type one of your recovery codes instead.')), error, submit);
  form.addEventListener('submit', async (e) => {
    e.preventDefault();
    error.hidden = true;
    submit.disabled = true;
    try {
      const user = await api('/auth/2fa/verify', { method: 'POST', body: { challenge, otp: otp.value } });
      onDone(user);
    } catch (err) {
      error.textContent = err.message;
      error.hidden = false;
      submit.disabled = false;
      otp.select();
      if (err.restart) setTimeout(onRestart, 1800);
    }
  });
  root.replaceChildren(form);
  otp.focus();
}
